import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { arrow, autoUpdate, flip, FloatingArrow, offset, Placement, safePolygon, shift, useFloating, useHover, useInteractions } from '@floating-ui/react';
import clsx from 'clsx';
import styles from './tooltip.module.styl';
type Props = {
  content: null | string | React.ReactElement;
  placement: Placement;
  /**
   * Whether to show the tooltip or not.
   * Sometimes we want to conditionally disable the tooltip. e.g. while dragging
   */
  enabled?: boolean;
  /** The element to add a tooltip to. also known as the "trigger" */
  children: React.ReactElement;
  maxWidth?: number | string;
  mode?: `tooltip` | `card`;
  /** 'true' to cause the tooltip to always show and never hide. */
  forceShow?: boolean;
};

/**
 * New Tooltip component using floating-ui.
 */
export function Tooltip({
  content,
  placement,
  enabled = true,
  maxWidth,
  children,
  mode = `tooltip`,
  forceShow = false
}: Props) {
  const [showTooltip, setShowTooltip] = useState(false);
  const arrowRef = useRef(null);
  const root = document.querySelector(`#tooltips`) || document.body;
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    placement,
    middleware: [flip({
      fallbackAxisSideDirection: `start`,
      crossAxis: false
    }),
    // Add spacing between tooltip/card and trigger element (8px for tooltips, default for cards)
    offset(mode === `tooltip` ? {
      mainAxis: 8
    } : undefined), shift(), arrow({
      element: arrowRef
    })],
    whileElementsMounted: autoUpdate,
    open: forceShow || showTooltip,
    onOpenChange: !forceShow ? setShowTooltip : undefined
  });
  const hover = useHover(context, {
    handleClose: safePolygon()
  });
  React.useEffect(() => {
    if (forceShow) {
      setShowTooltip(true);
    }
  }, [forceShow, setShowTooltip]);
  const {
    getFloatingProps
  } = useInteractions([hover]);
  const trigger = children ? React.cloneElement(children, {
    ref: (el: any) => {
      refs.setReference(el);

      // preserve any ref that might be passed in to the child
      const originalRef = (children as any)?.ref;
      if (typeof originalRef === `function`) {
        originalRef(el);
      } else if (originalRef) {
        originalRef.current = el;
      }
    }
  }) : null;
  return <>
      {trigger}
      {showTooltip && content && enabled && ReactDOM.createPortal(<div className={styles.tooltipContainer} ref={refs.setFloating} style={{
      ...floatingStyles
      // maxWidth,
    }} {...getFloatingProps()}>
            <div className={clsx({
        [styles.tooltip]: true,
        [styles.cardMode]: mode === `card`
      })}>
              {mode === `tooltip` && <FloatingArrow ref={arrowRef} context={context} fill={`var(--inverted-component-background-color)`} />}
              <div className={clsx({
          [styles.tooltipContent]: true,
          [styles.hasMaxWidth]: Boolean(maxWidth)
        })} style={{
          maxWidth
        }}>
                {content}
              </div>
            </div>
          </div>, root)}
    </>;
}